import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';
import {
  ArrowLeft,
  Crown,
  Save,
  Loader2,
  Eye,
  ArrowUp,
  ArrowDown,
  Plus,
  Trash2,
  ExternalLink,
  Star,
  RotateCcw,
} from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

// All editable text fields, grouped per section, with their CMS keys.
const SECTION_DEFS = [
  {
    id: 'hero',
    title: 'Cabeçalho (Hero)',
    description: 'Etiqueta, título principal, subtítulo e texto do botão no topo da página.',
    fields: [
      { key: 'premium.hero.badge', label: 'Etiqueta (badge)', type: 'text' },
      { key: 'premium.hero.title', label: 'Título principal', type: 'text' },
      { key: 'premium.hero.subtitle', label: 'Subtítulo', type: 'textarea' },
      { key: 'premium.hero.cta', label: 'Texto do botão principal', type: 'text' },
    ],
  },
  {
    id: 'benefits',
    title: 'Benefícios (O que está incluído)',
    description: 'Lista visual de benefícios. Cada linha torna-se um item.',
    fields: [
      { key: 'premium.benefits.title', label: 'Título', type: 'text' },
      { key: 'premium.benefits.subtitle', label: 'Subtítulo (opcional)', type: 'textarea' },
      { key: 'premium.benefits.items', label: 'Benefícios (um por linha)', type: 'textarea' },
    ],
  },
  {
    id: 'above',
    title: 'Conteúdo Acima dos Preços (rich text)',
    description: 'Bloco de HTML opcional entre os benefícios e os planos.',
    fields: [
      { key: 'premium.above.content', label: 'Conteúdo (rich text)', type: 'html' },
    ],
  },
  {
    id: 'plans',
    title: 'Planos e CTAs',
    description: 'Texto dos botões. Os planos em si gerem-se mais abaixo.',
    fields: [
      { key: 'premium.cta.button', label: 'Texto do botão "Assinar"', type: 'text' },
      { key: 'premium.cta.processing', label: 'Texto durante o processamento', type: 'text' },
    ],
  },
  {
    id: 'notice',
    title: 'Aviso Importante',
    description: 'Aviso destacado a explicar que o email deve coincidir com o registo na Intimate.pt.',
    fields: [
      { key: 'premium.notice.content', label: 'Texto do aviso', type: 'textarea' },
    ],
  },
  {
    id: 'how-it-works',
    title: 'Como funciona (3 passos)',
    description: 'Pequeno guia em 3 passos para explicar o fluxo de compra.',
    fields: [
      { key: 'premium.howitworks.title', label: 'Título da secção', type: 'text' },
      { key: 'premium.howitworks.step1', label: 'Passo 1', type: 'text' },
      { key: 'premium.howitworks.step2', label: 'Passo 2', type: 'text' },
      { key: 'premium.howitworks.step3', label: 'Passo 3', type: 'text' },
    ],
  },
  {
    id: 'below',
    title: 'Conteúdo Abaixo dos Preços (rich text)',
    description: 'Bloco de HTML opcional depois dos planos.',
    fields: [
      { key: 'premium.below.content', label: 'Conteúdo (rich text)', type: 'html' },
    ],
  },
  {
    id: 'faq',
    title: 'Perguntas Frequentes (FAQ)',
    description: 'Título e lista de perguntas em rich-text (usa <h3> para cada pergunta).',
    fields: [
      { key: 'premium.faq.title', label: 'Título da secção FAQ', type: 'text' },
      { key: 'premium.faq.content', label: 'Conteúdo do FAQ (rich text)', type: 'html' },
    ],
  },
  {
    id: 'final-cta',
    title: 'CTA Final',
    description: 'Bloco de conversão final com o título, subtítulo e os 3 botões de plano.',
    fields: [
      { key: 'premium.finalcta.title', label: 'Título', type: 'text' },
      { key: 'premium.finalcta.subtitle', label: 'Subtítulo', type: 'textarea' },
    ],
  },
];

const ALREADY_PREMIUM_FIELDS = [
  { key: 'premium.already.title', label: 'Título', type: 'text' },
  { key: 'premium.already.subtitle', label: 'Subtítulo', type: 'textarea' },
  { key: 'premium.already.button', label: 'Texto do botão', type: 'text' },
];

const BADGE_CLASS_OPTIONS = [
  { value: 'none', label: 'Sem destaque' },
  { value: 'ruby', label: 'Vermelho (Ruby)' },
  { value: 'gold', label: 'Dourado (Gold)' },
];

function emptyPlan() {
  return {
    plan_id: '',
    name: '',
    description: '',
    amount: 0,
    duration_days: 30,
    period_label: '',
    badge_label: '',
    badge_class: 'none',
    sort_order: 999,
    is_visible: true,
    is_highlight: false,
    cta_label: '',
    cta_url: '',
  };
}

export default function AdminPremiumEditor() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [savingLayout, setSavingLayout] = useState(false);

  // Content blocks (CMS) state
  const [content, setContent] = useState({}); // key -> server value
  const [edited, setEdited] = useState({}); // key -> edited value
  const [missingKeys, setMissingKeys] = useState([]); // keys not yet existing in DB

  // Section layout
  const [sections, setSections] = useState([]);

  // Plans
  const [plans, setPlans] = useState([]);

  // Plan dialog state
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState(emptyPlan());
  const [savingPlan, setSavingPlan] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    loadAll();
  }, [user, navigate]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [blocksRes, layoutRes, plansRes] = await Promise.all([
        api.get('/admin/cms/content', { params: { page: 'premium' } }),
        api.get('/cms/premium-layout'),
        api.get('/admin/premium-plans'),
      ]);

      // Build map of existing CMS keys
      const map = {};
      blocksRes.data.forEach((b) => {
        map[b.key] = b.content;
      });

      // Detect missing keys (need to be created)
      const allDefinedKeys = [
        ...SECTION_DEFS.flatMap((s) => s.fields.map((f) => f.key)),
        ...ALREADY_PREMIUM_FIELDS.map((f) => f.key),
      ];
      const missing = allDefinedKeys.filter((k) => !(k in map));

      setContent(map);
      setEdited({ ...map });
      setMissingKeys(missing);
      setSections(layoutRes.data?.sections || []);
      setPlans(plansRes.data || []);

      // If there are missing keys, auto-seed them via public GET endpoint
      // (it auto-seeds defaults on first call) and re-fetch.
      if (missing.length > 0) {
        await api.get('/cms/content/premium').catch(() => null);
        const r2 = await api.get('/admin/cms/content', { params: { page: 'premium' } });
        const m2 = {};
        r2.data.forEach((b) => {
          m2[b.key] = b.content;
        });
        setContent(m2);
        setEdited({ ...m2 });
        setMissingKeys(allDefinedKeys.filter((k) => !(k in m2)));
      }
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao carregar dados'));
    } finally {
      setLoading(false);
    }
  };

  const dirty = (key) => edited[key] !== content[key];

  const onChangeField = (key, value) => {
    setEdited((prev) => ({ ...prev, [key]: value }));
  };

  const saveField = async (key, contentType) => {
    setSavingKey(key);
    try {
      if (!(key in content)) {
        // Create the block on first save (rare — only if seed missed it)
        await api.post('/admin/cms/content', {
          key,
          content: edited[key] ?? '',
          content_type: contentType === 'html' ? 'html' : 'text',
          page: 'premium',
          section: key.split('.')[1] || 'misc',
          label: key,
        });
      } else {
        await api.put(`/admin/cms/content/${key}`, { content: edited[key] ?? '' });
      }
      setContent((prev) => ({ ...prev, [key]: edited[key] }));
      toast.success('Guardado');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao guardar'));
    } finally {
      setSavingKey(null);
    }
  };

  // ----- Section layout (visibility + order) -----
  const moveSection = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[index], next[target]] = [next[target], next[index]];
    setSections(next);
  };

  const toggleSectionVisible = (id) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, visible: !s.visible } : s)));
  };

  const saveLayout = async () => {
    setSavingLayout(true);
    try {
      await api.put('/admin/cms/premium-layout', { sections });
      toast.success('Layout atualizado');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao guardar layout'));
    } finally {
      setSavingLayout(false);
    }
  };

  const resetLayout = async () => {
    if (!window.confirm('Repor a ordem e visibilidade das secções para os valores por omissão?')) return;
    try {
      const res = await api.post('/admin/cms/premium-layout/reset');
      setSections(res.data?.sections || []);
      toast.success('Layout reposto');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao repor layout'));
    }
  };

  // ----- Plans -----
  const openCreatePlan = () => {
    setEditingPlan(null);
    setPlanForm({ ...emptyPlan(), sort_order: plans.length });
    setPlanDialogOpen(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      plan_id: plan.plan_id,
      name: plan.name || '',
      description: plan.description || '',
      amount: Number(plan.amount || 0),
      duration_days: Number(plan.duration_days || 30),
      period_label: plan.period_label || '',
      badge_label: plan.badge_label || '',
      badge_class: plan.badge_class || 'none',
      sort_order: Number(plan.sort_order ?? 0),
      is_visible: plan.is_visible !== false,
      is_highlight: !!plan.is_highlight,
      cta_label: plan.cta_label || '',
      cta_url: plan.cta_url || '',
    });
    setPlanDialogOpen(true);
  };

  const submitPlan = async (e) => {
    e?.preventDefault?.();
    if (!planForm.plan_id || !planForm.name) {
      toast.error('Preenche o identificador e o nome do plano');
      return;
    }
    if (planForm.amount < 0) {
      toast.error('Valor inválido');
      return;
    }
    if (planForm.duration_days < 1) {
      toast.error('Duração inválida');
      return;
    }
    setSavingPlan(true);
    try {
      if (editingPlan) {
        const { plan_id, ...rest } = planForm;
        await api.put(`/admin/premium-plans/${editingPlan.plan_id}`, rest);
        toast.success('Plano atualizado');
      } else {
        await api.post('/admin/premium-plans', planForm);
        toast.success('Plano criado');
      }
      setPlanDialogOpen(false);
      const res = await api.get('/admin/premium-plans');
      setPlans(res.data || []);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao guardar plano'));
    } finally {
      setSavingPlan(false);
    }
  };

  const deletePlan = async (plan) => {
    if (!window.confirm(`Eliminar o plano "${plan.name}"? Esta ação é permanente.`)) return;
    try {
      await api.delete(`/admin/premium-plans/${plan.plan_id}`);
      setPlans((prev) => prev.filter((p) => p.plan_id !== plan.plan_id));
      toast.success('Plano eliminado');
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao eliminar plano'));
    }
  };

  const movePlan = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= plans.length) return;
    const next = [...plans];
    [next[index], next[target]] = [next[target], next[index]];
    setPlans(next);
    try {
      const order = next.map((p) => p.plan_id);
      const res = await api.post('/admin/premium-plans/reorder', { order });
      setPlans(res.data || next);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao reordenar'));
    }
  };

  const togglePlanVisible = async (plan) => {
    try {
      const res = await api.put(`/admin/premium-plans/${plan.plan_id}`, {
        is_visible: !plan.is_visible,
      });
      setPlans((prev) => prev.map((p) => (p.plan_id === plan.plan_id ? res.data : p)));
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar visibilidade'));
    }
  };

  const togglePlanHighlight = async (plan) => {
    try {
      const res = await api.put(`/admin/premium-plans/${plan.plan_id}`, {
        is_highlight: !plan.is_highlight,
      });
      setPlans((prev) => prev.map((p) => (p.plan_id === plan.plan_id ? res.data : p)));
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar destaque'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-foreground"
              data-testid="premium-editor-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-ruby/20 flex items-center justify-center">
                <Crown className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Editor da Página Premium</h1>
                <p className="text-muted-foreground">
                  Edita textos, planos e ordem das secções sem tocar em código.
                </p>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => window.open('/premium', '_blank', 'noopener')}
            className="border-border"
            data-testid="premium-editor-preview"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Ver página
          </Button>
        </div>

        {/* Layout / sections */}
        <Card className="bg-surface border-border mb-8">
          <CardHeader>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <CardTitle className="text-foreground">Estrutura da página</CardTitle>
                <CardDescription>
                  Mostra, esconde ou reordena cada secção. As alterações aplicam-se imediatamente após guardar.
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={resetLayout}
                  className="border-border"
                  data-testid="layout-reset"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Repor
                </Button>
                <Button
                  onClick={saveLayout}
                  disabled={savingLayout}
                  className="bg-ruby hover:bg-ruby-light"
                  data-testid="layout-save"
                >
                  {savingLayout ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Guardar ordem
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {sections.map((s, idx) => {
              const def = SECTION_DEFS.find((d) => d.id === s.id);
              return (
                <div
                  key={s.id}
                  className="flex items-center gap-3 p-3 bg-background rounded-lg border border-border"
                  data-testid={`section-row-${s.id}`}
                >
                  <span className="text-xs font-mono text-muted-foreground w-6 text-center">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">{def?.title || s.id}</p>
                    {def?.description && (
                      <p className="text-xs text-muted-foreground truncate">{def.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={s.visible !== false}
                      onCheckedChange={() => toggleSectionVisible(s.id)}
                      data-testid={`section-toggle-${s.id}`}
                    />
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                      {s.visible !== false ? 'Visível' : 'Oculta'}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => moveSection(idx, -1)}
                      disabled={idx === 0}
                      className="border-border h-8 w-8"
                      data-testid={`section-up-${s.id}`}
                    >
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() => moveSection(idx, 1)}
                      disabled={idx === sections.length - 1}
                      className="border-border h-8 w-8"
                      data-testid={`section-down-${s.id}`}
                    >
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Section content editors */}
        {SECTION_DEFS.map((sec) => (
          <Card key={sec.id} className="bg-surface border-border mb-6" data-testid={`editor-${sec.id}`}>
            <CardHeader>
              <CardTitle className="text-foreground flex items-center gap-2">
                {sec.title}
              </CardTitle>
              <CardDescription>{sec.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {sec.fields.map((field) => (
                <div key={field.key} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-foreground">{field.label}</Label>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => saveField(field.key, field.type)}
                      disabled={!dirty(field.key) || savingKey === field.key}
                      className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                      data-testid={`save-${field.key}`}
                    >
                      {savingKey === field.key ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-1" />
                      )}
                      Guardar
                    </Button>
                  </div>
                  {field.type === 'html' ? (
                    <RichTextEditor
                      content={edited[field.key] || ''}
                      onChange={(value) => onChangeField(field.key, value)}
                      placeholder="Conteúdo…"
                    />
                  ) : field.type === 'textarea' ? (
                    <Textarea
                      value={edited[field.key] || ''}
                      onChange={(e) => onChangeField(field.key, e.target.value)}
                      className="bg-background border-border min-h-[100px]"
                      data-testid={`field-${field.key}`}
                    />
                  ) : (
                    <Input
                      value={edited[field.key] || ''}
                      onChange={(e) => onChangeField(field.key, e.target.value)}
                      className="bg-background border-border"
                      data-testid={`field-${field.key}`}
                    />
                  )}
                  {dirty(field.key) && (
                    <p className="text-xs text-gold">* alterações não guardadas</p>
                  )}
                </div>
              ))}

              {/* Plans manager for the "plans" section */}
              {sec.id === 'plans' && (
                <div className="pt-4 mt-2 border-t border-border">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div>
                      <h3 className="font-medium text-foreground">Planos de subscrição</h3>
                      <p className="text-xs text-muted-foreground">
                        Cria, edita, reordena, esconde ou destaca os planos exibidos.
                      </p>
                    </div>
                    <Button
                      onClick={openCreatePlan}
                      className="bg-ruby hover:bg-ruby-light"
                      data-testid="plan-create"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar plano
                    </Button>
                  </div>

                  {plans.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-6 text-center">
                      Ainda não existem planos. Adiciona o primeiro.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {plans.map((plan, idx) => (
                        <div
                          key={plan.plan_id}
                          className="p-4 bg-background border border-border rounded-lg"
                          data-testid={`plan-row-${plan.plan_id}`}
                        >
                          <div className="flex items-start gap-3 flex-wrap">
                            <div className="flex-1 min-w-[200px]">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-foreground">{plan.name}</span>
                                <Badge variant="outline" className="border-border font-mono text-xs">
                                  {plan.plan_id}
                                </Badge>
                                {plan.is_highlight && (
                                  <Badge className="bg-gold/20 text-gold border border-gold/40">
                                    <Star className="w-3 h-3 mr-1" /> Destacado
                                  </Badge>
                                )}
                                {plan.badge_label && plan.badge_class && plan.badge_class !== 'none' && (
                                  <Badge
                                    className={
                                      plan.badge_class === 'gold'
                                        ? 'bg-gold text-black'
                                        : 'bg-ruby text-white'
                                    }
                                  >
                                    {plan.badge_label}
                                  </Badge>
                                )}
                                {!plan.is_visible && (
                                  <Badge variant="outline" className="border-muted text-muted-foreground">
                                    Oculto
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground mt-1">
                                € {Number(plan.amount || 0).toFixed(2).replace('.', ',')}
                                {plan.period_label ? ` ${plan.period_label}` : ''} · {plan.duration_days} dias
                              </p>
                              {plan.description && (
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {plan.description}
                                </p>
                              )}
                              {(plan.cta_label || plan.cta_url) && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Botão: <span className="text-foreground">{plan.cta_label || 'Assinar'}</span>
                                  {plan.cta_url && (
                                    <>
                                      {' '}→ <span className="font-mono text-foreground/80 break-all">{plan.cta_url}</span>
                                    </>
                                  )}
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-2">
                              <div className="flex flex-col items-center text-xs text-muted-foreground gap-1">
                                <span>Visível</span>
                                <Switch
                                  checked={plan.is_visible !== false}
                                  onCheckedChange={() => togglePlanVisible(plan)}
                                  data-testid={`plan-toggle-visible-${plan.plan_id}`}
                                />
                              </div>
                              <div className="flex flex-col items-center text-xs text-muted-foreground gap-1">
                                <span>Destaque</span>
                                <Switch
                                  checked={!!plan.is_highlight}
                                  onCheckedChange={() => togglePlanHighlight(plan)}
                                  data-testid={`plan-toggle-highlight-${plan.plan_id}`}
                                />
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="icon"
                                  variant="outline"
                                  onClick={() => movePlan(idx, -1)}
                                  disabled={idx === 0}
                                  className="border-border h-8 w-8"
                                  data-testid={`plan-up-${plan.plan_id}`}
                                >
                                  <ArrowUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="outline"
                                  onClick={() => movePlan(idx, 1)}
                                  disabled={idx === plans.length - 1}
                                  className="border-border h-8 w-8"
                                  data-testid={`plan-down-${plan.plan_id}`}
                                >
                                  <ArrowDown className="w-4 h-4" />
                                </Button>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditPlan(plan)}
                                className="border-border"
                                data-testid={`plan-edit-${plan.plan_id}`}
                              >
                                Editar
                              </Button>
                              <Button
                                size="icon"
                                variant="outline"
                                onClick={() => deletePlan(plan)}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 w-8"
                                data-testid={`plan-delete-${plan.plan_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {/* Already premium message */}
        <Card className="bg-surface border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">Mensagem para Assinantes Atuais</CardTitle>
            <CardDescription>
              Texto exibido a quem já tem Premium ativo ao visitar a página.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {ALREADY_PREMIUM_FIELDS.map((field) => (
              <div key={field.key} className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-foreground">{field.label}</Label>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => saveField(field.key, field.type)}
                    disabled={!dirty(field.key) || savingKey === field.key}
                    className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                    data-testid={`save-${field.key}`}
                  >
                    {savingKey === field.key ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-1" />
                    )}
                    Guardar
                  </Button>
                </div>
                {field.type === 'textarea' ? (
                  <Textarea
                    value={edited[field.key] || ''}
                    onChange={(e) => onChangeField(field.key, e.target.value)}
                    className="bg-background border-border min-h-[80px]"
                    data-testid={`field-${field.key}`}
                  />
                ) : (
                  <Input
                    value={edited[field.key] || ''}
                    onChange={(e) => onChangeField(field.key, e.target.value)}
                    className="bg-background border-border"
                    data-testid={`field-${field.key}`}
                  />
                )}
                {dirty(field.key) && (
                  <p className="text-xs text-gold">* alterações não guardadas</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Plan Dialog */}
        <Dialog open={planDialogOpen} onOpenChange={setPlanDialogOpen}>
          <DialogContent className="bg-surface border-border max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-foreground">
                {editingPlan ? 'Editar plano' : 'Novo plano'}
              </DialogTitle>
              <DialogDescription>
                Define o preço, duração, etiqueta e visibilidade do plano. As alterações são imediatas na página Premium.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitPlan} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Identificador (slug) *</Label>
                  <Input
                    value={planForm.plan_id}
                    onChange={(e) => setPlanForm({ ...planForm, plan_id: e.target.value })}
                    placeholder="ex: quarterly"
                    disabled={!!editingPlan}
                    className="bg-background border-border font-mono"
                    data-testid="plan-form-id"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nome do plano *</Label>
                  <Input
                    value={planForm.name}
                    onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                    placeholder="ex: Premium 6 Meses"
                    className="bg-background border-border"
                    data-testid="plan-form-name"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descrição</Label>
                <Textarea
                  value={planForm.description}
                  onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                  placeholder="Acesso ilimitado durante…"
                  className="bg-background border-border"
                  data-testid="plan-form-description"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Preço (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={planForm.amount}
                    onChange={(e) => setPlanForm({ ...planForm, amount: parseFloat(e.target.value) || 0 })}
                    className="bg-background border-border"
                    data-testid="plan-form-amount"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Duração (dias) *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={planForm.duration_days}
                    onChange={(e) => setPlanForm({ ...planForm, duration_days: parseInt(e.target.value) || 1 })}
                    className="bg-background border-border"
                    data-testid="plan-form-duration"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Período (texto)</Label>
                  <Input
                    value={planForm.period_label}
                    onChange={(e) => setPlanForm({ ...planForm, period_label: e.target.value })}
                    placeholder="ex: / 6 meses"
                    className="bg-background border-border"
                    data-testid="plan-form-period"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Etiqueta (badge)</Label>
                  <Input
                    value={planForm.badge_label}
                    onChange={(e) => setPlanForm({ ...planForm, badge_label: e.target.value })}
                    placeholder="ex: Mais Popular"
                    className="bg-background border-border"
                    data-testid="plan-form-badge-label"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cor da etiqueta</Label>
                  <Select
                    value={planForm.badge_class}
                    onValueChange={(v) => setPlanForm({ ...planForm, badge_class: v })}
                  >
                    <SelectTrigger className="bg-background border-border" data-testid="plan-form-badge-class">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BADGE_CLASS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Texto do botão (opcional)</Label>
                  <Input
                    value={planForm.cta_label}
                    onChange={(e) => setPlanForm({ ...planForm, cta_label: e.target.value })}
                    placeholder='Padrão: "Assinar"'
                    className="bg-background border-border"
                    data-testid="plan-form-cta-label"
                  />
                  <p className="text-xs text-muted-foreground">Substitui o texto global apenas neste plano.</p>
                </div>
                <div className="space-y-2">
                  <Label>Link do botão (opcional)</Label>
                  <Input
                    value={planForm.cta_url}
                    onChange={(e) => setPlanForm({ ...planForm, cta_url: e.target.value })}
                    placeholder="ex: https://… ou /pagina"
                    className="bg-background border-border"
                    data-testid="plan-form-cta-url"
                  />
                  <p className="text-xs text-muted-foreground">
                    Se preenchido, o botão abre este link em vez de iniciar o checkout. URLs externas abrem em nova aba.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={planForm.is_visible}
                    onCheckedChange={(v) => setPlanForm({ ...planForm, is_visible: v })}
                    data-testid="plan-form-visible"
                  />
                  <span className="text-sm text-foreground">Visível na página</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={planForm.is_highlight}
                    onCheckedChange={(v) => setPlanForm({ ...planForm, is_highlight: v })}
                    data-testid="plan-form-highlight"
                  />
                  <span className="text-sm text-foreground">Destacar (cor especial)</span>
                </label>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPlanDialogOpen(false)}
                  className="border-border"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={savingPlan}
                  className="bg-ruby hover:bg-ruby-light"
                  data-testid="plan-form-submit"
                >
                  {savingPlan ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  {editingPlan ? 'Atualizar' : 'Criar plano'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
